import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LostItem } from "@/types";
import { Signature, Info } from "lucide-react";
import StatusTimeline from "@/components/shared/StatusTimeline";
import { useState, useEffect } from "react";
import { supabase } from '@/integrations/supabase/client';

interface LostItemInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: LostItem | null;
}
const badgeStyle = {
  Pending: "bg-purple-100 text-purple-500",
  Delivered: "bg-green-100 text-green-500",
  Discarded: "bg-orange-100 text-orange-500",
  Deleted: "bg-gray-100 text-gray-400",
};
const LostItemInfoModal = ({
  open,
  onOpenChange,
  item
}: LostItemInfoModalProps) => {
  const [signedPhotoUrl, setSignedPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function fetchSignedUrl() {
      if (item?.photo && !item.photo.startsWith('data:')) {
        const { data, error } = await supabase.storage.from('lost-and-found-photos').createSignedUrl(item.photo, 60 * 60);
        if (isMounted && data?.signedUrl) setSignedPhotoUrl(data.signedUrl);
      } else {
        setSignedPhotoUrl(null);
      }
    }
    fetchSignedUrl();
    return () => { isMounted = false; };
  }, [item?.photo]);

  if (!item) return null;
  // Build a full history array for the timeline
  let history = [];
  if (item.history && item.history.length > 0) {
    history = item.history;
  } else {
    // Fallback: show only creation event if no history
    history = [
      { status: 'Created', date: item.date, updatedBy: 'unknown' }
    ];
    if (item.status !== 'Created') {
      history.push({ status: item.status, date: item.date, updatedBy: 'unknown' });
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Lost Item Information
          </DialogTitle>
          <DialogClose className="absolute right-4 top-4">
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="space-y-6 p-2">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Registered ID</div>
              <div className="font-semibold text-2xl leading-tight">{String(item.sequential_number).padStart(3, '0')}</div>
              <span className={`inline-block rounded px-2 py-1 text-xs font-bold mt-1 ${badgeStyle[item.status]}`}>{item.status}</span>
            </div>
            {item.photo && (item.photo.startsWith('data:') || signedPhotoUrl) && (
              <img src={item.photo.startsWith('data:') ? item.photo : signedPhotoUrl || ''} alt="Item" className="w-20 h-20 object-cover rounded shadow border" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Description</div>
              <div className="text-sm font-medium break-words">{item.description}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Location</div>
              <div className="text-sm">{(item as any).location || 'N/A'}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Date Registered</div>
              <div className="text-sm">{item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</div>
            </div>
            <div>
              <div className="text-xs mb-1 text-muted-foreground">Found by</div>
              <div className="text-sm">{(item as any).found_by || 'N/A'}</div>
            </div>
          </div>
          <StatusTimeline history={history} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default LostItemInfoModal;
